import { Component, AfterViewInit, AfterContentInit } from "@angular/core";
import { ServiceConfigService } from "@app/services/service-config.service";
import { SectionFormDirective } from "@app/classes/section-form.class";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Observable, Subscription } from "rxjs";
import { IGetCoverageDetails } from "@app/models/endCoverage.model";
import { TopLevelAnswer } from "@app/models/section.model";
import { Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { formatTableData } from "@app/helpers/tables.helpers";
import { StateService } from "@app/services/state/state.service";
import { ApplicationService } from "@app/services/application/application.service";
import { GoogleAnalyticsService } from "@app/services/analytics/analytics.service";

@Component({
    templateUrl: "./end-coverage.component.html",
    styleUrls: ["./end-coverage.component.scss"],
    standalone: false
})

export class EndCoverageComponent extends SectionFormDirective implements AfterContentInit, AfterViewInit {
  errorMessage: boolean;
  infoMessage: any = false;
  subscription: Subscription;
  hohId: any;
  ssiEligibleMessage = false;
  ssiName: string;
  endCoverageCheckControls = false;
  isOnlyEndCoverageTileSelected: boolean = false;
  coverageCount: IGetCoverageDetails[] = [];
  selectedIndivList = [];
  selectedAll = false;
  valuesToModal = [];
  householdMembers;
  members;
  rmcVolTermChangeSwitch: boolean = false;
  terminateCoverageForm!: FormGroup;
  showMembers: boolean = false;
  translatedLang: string;
  defaultLanguage: string;


  constructor(builder: FormBuilder, private titleService: Title, private serverConfig: ServiceConfigService, public translateService: TranslateService, private state: StateService, private applicationService: ApplicationService, private analytics: GoogleAnalyticsService) {
    super(builder);
    this.titleService.setTitle('End Coverage');
    this.terminateCoverageForm = builder.group({
      calledYes: [null],
      calledNo: [null]
    });
  }

  ngAfterContentInit() {
    this.rmcVolTermChangeSwitch = this.serverConfig.getServerConfig()?.rmcVolTermChangeSwitch;
    this.infoMessage = true;
    this.householdMembers = this.getValue('data.householdList');
    this.isOnlyEndCoverageTileSelected = this.getValue('data.isOnlyEndCoverageTileSelected');
    if (this.rmcVolTermChangeSwitch) {
      this.members = this.householdMembers.filter((item: any) => item.headOfHousehold === 'N' && item.applyForCvrgSw === 'Y' && item.volTermSw === 'Y');
      this.hohId = this.householdMembers.filter((item: any) => item.headOfHousehold === 'Y' && item.applyForCvrgSw === 'Y' && item.volTermSw === 'Y');
    } else {
      this.members = this.householdMembers.filter((item: any) => item.headOfHousehold === 'N');
      this.hohId = this.householdMembers.filter((item: any) => item.headOfHousehold === 'Y');
    }
    this.coverageCount = this.members.length + this.hohId.length;
    this.selectedIndivList = this.householdMembers.filter((indv:any) => indv.deleteFlag === 'true');
    if (this.rmcVolTermChangeSwitch) {
      const isEligible = !!(this.members.length || this.hohId?.length);
      if (!isEligible) {
        this.analytics.trackEndCoverageEvent(this.isOnlyEndCoverageTileSelected ? 'rmc_endcoverage_ineligible_direct' : 'rmc_endcoverage_ineligible_combined');
      }
      const endCvgOrMoveOutSw = this.getValue('data.endCvgOrMoveOutSw');
      if (endCvgOrMoveOutSw === 'Y') {
        this.terminateCoverageForm?.get('calledYes')?.setValue('checked');
        this.getControl('extras.helpers.termChoice')?.setValue('yes');
        this.showMembers = true;
      } else if (endCvgOrMoveOutSw === 'N') {
        this.terminateCoverageForm?.get('calledNo')?.setValue('checked');
        this.getControl('extras.helpers.termChoice')?.setValue('no');
      }
    }
    this.defaultLanguage = this.translateService.getDefaultLang();
    this.languageCheck(this.defaultLanguage);
    this.translatedLang = this.defaultLanguage;

    // Restore state when returning to a previously completed screen
    if (this.rmcVolTermChangeSwitch && this.showMembers) {
      const ctrl = this.getControl('data.selectedList');
      this.selectedIndivList.forEach(indv => ctrl.value.push(indv));
      // Patch moveSomeoneOutSw with the matching table object so the dropdown pre-populates.
      const savedVal = this.getValue('data.moveSomeoneOutSw');
      const codeToMatch = typeof savedVal === 'string' ? savedVal : savedVal?.code;
      if (codeToMatch) {
        const matchingItem = this.tables?.['YESNO']?.find((item: any) => item.code === codeToMatch);
        if (matchingItem) {
          this.getControl('data.moveSomeoneOutSw')?.setValue(matchingItem);
        }
      }
    }

    this.translateService.onDefaultLangChange.subscribe((res) => {
      this.translatedLang = res.lang
      this.languageCheck(res.lang);
    })
  }

  ngAfterViewInit(): void {
    window.scroll(0,0);
  }

  selectedAction(value) {
    const ctrl = this.getControl('data.selectedList');

    this.errorMessage = false;
    this.ssiEligibleMessage = false;
    if (this.endCoverageCheckControls) {
      this.selectedAll = false;
      this.selectedIndivList = [];
      return;
    }

    const hohVal = value.headOfHousehold ? value.headOfHousehold : value[0].headOfHousehold;
    if (this.hohId.length && this.coverageCount && hohVal === this.hohId[0].headOfHousehold) {
      this.selectedAll = !this.selectedAll;
      this.selectedIndivList = [];
      ctrl.setValue([]);
      if (this.selectedAll) {
        this.householdMembers.forEach((individual:any) => {
          this.selectedIndivList.push(individual);
          ctrl.value.push(individual);
        });
      } else {
        this.selectedIndivList = [];
        ctrl.setValue([]);
      }
    } else {
      if (this.selectedAll) {
        this.selectedAll = false;
        this.selectedIndivList = [];
        ctrl.setValue([]);
        this.selectedIndivList.push(value);
        ctrl.value.push(value);
      } else {
        if (!this.selectedIndivList.includes(value)) {
          this.selectedIndivList.push(value);
          ctrl.value.push(value);
        } else {
          const indexToDel = this.selectedIndivList.indexOf(value);
          this.selectedIndivList.splice(indexToDel, 1);
          ctrl.value.splice(indexToDel, 1);
        }
      }
    }
    if (this.rmcVolTermChangeSwitch) {
      if (this.selectedIndivList.length === 0) {
        this.getControl('data.moveSomeoneOutSw')?.reset();
      }
      ctrl.updateValueAndValidity();
    }
  }

  selectedTile(indiv) {
    const selectedIndvi = indiv.indvNum ? indiv.indvNum : indiv[0]?.indvNum;
    const selectedList = this.selectedIndivList.map(i => i.indvNum);

    return selectedList.includes(selectedIndvi);
  }

  deselectMembers() {
    const ctrl = this.getControl('data.selectedList');
    this.selectedAll = false;
    this.selectedIndivList = [];
    ctrl.setValue([]);
    if (this.rmcVolTermChangeSwitch) {
      this.getControl('data.moveSomeoneOutSw')?.reset();
    }
  }

  onCheckboxChange(checkboxName: string): void {
    const isChecked = (name: string) => this.terminateCoverageForm?.get(name)?.value === 'checked';
    if (checkboxName === 'calledYes') {
      this.terminateCoverageForm?.get('calledNo')?.setValue(null);
      if (isChecked('calledYes')) {
        this.getControl('extras.helpers.termChoice')?.setValue('yes');
        this.getControl('data.endCvgOrMoveOutSw')?.setValue('Y');
        this.showMembers = true;
      } else {
        this.getControl('extras.helpers.termChoice')?.setValue(null);
        this.getControl('data.endCvgOrMoveOutSw')?.setValue(null);
        this.showMembers = false;
      }
    } else if (checkboxName === 'calledNo') {
      this.terminateCoverageForm?.get('calledYes')?.setValue(null);
      // Clear any previously selected members
      const ctrl = this.getControl('data.selectedList');
      this.selectedAll = false;
      this.selectedIndivList = [];
      ctrl?.setValue([]);
      this.getControl('data.moveSomeoneOutSw')?.reset();
      this.showMembers = false;
      if (isChecked('calledNo')) {
        this.getControl('extras.helpers.termChoice')?.setValue('no');
        this.getControl('data.endCvgOrMoveOutSw')?.setValue('N');
      } else {
        this.getControl('extras.helpers.termChoice')?.setValue(null);
        this.getControl('data.endCvgOrMoveOutSw')?.setValue(null);
      }
    }
  }

   tableCodeValue(tableName: string, tableControl: string) {
    return this.getTableCodeValues(tableName, tableControl, this.translatedLang, true)
  }

  languageCheck(language:string) {
    this.tables = language === 'sp' ? formatTableData(this.tables, 'spDescription') : formatTableData(this.tables, 'description');
  }

  private saveEndCoverageNo(): Observable<any> {
    const appNumber = sessionStorage.getItem('appNum');
    const householdList = this.householdMembers || [];
    const entityDetails = householdList.map((member: any) => ({
      appNum: appNumber,
      indvId: member.indvNum,
      caseNum: member.caseNum,
      srcAppInd: 'C',
      deleteFlag: false
    }));
    const record = {
      identifier: this.record.identifier,
      data: { data: entityDetails, endCvgOrMoveOutSw: 'N' },
      exportingStatus: ''
    };
    return this.status === 'IN_PROGRESS'
      ? this.applicationService.createRecord(this.applicationNumber, record, TopLevelAnswer.YES)
      : this.applicationService.updateRecord(this.applicationNumber, record);
  }

  goToMainScreen() {
    this.saveEndCoverageNo().subscribe(() => {
      this.state.navigate(this.applicationNumber, 'SURCH');
      this.analytics.trackEndCoverageEvent('rmc_volterm_backto_main');
    });
  }

  goToHohScreen() {
    this.saveEndCoverageNo().subscribe(() => {
    this.applicationService.getSection(this.applicationNumber, 'SURCH').subscribe((response: any) => {
    this.analytics.trackEndCoverageEvent('rmc_endCoverage_hohSelected');
      const record = response.records[0];
      const modules = record.data.modules;

      const hhppg = modules.find((m: any) => m.moduleId === 'HHPPG');
      if (hhppg) {
        hhppg.sections.forEach((s: any) => {
          if (s.sectionId === 'HHIND') {
            s.changeRequested = true;
          }
        });
      }

      const currentLang = sessionStorage.getItem('currentLang');
      const payload = {
        ...record.data,
        modules,
        languageCode: currentLang === 'sp' ? 'ES' : 'EN'
      };

      this.applicationService.updateRecord(this.applicationNumber, {
        identifier: record.identifier,
        data: payload,
        exportingStatus: record.exportingStatus
      }).subscribe(() => {
        this.state.loadApp(this.applicationNumber).subscribe(() => {
          this.state.navigate(this.applicationNumber, 'HHIND');
        });
      });
    });
    });
  }
}
